import React, { useCallback, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton, LinkButton, LinkIconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { fg } from '@atlaskit/platform-feature-flags';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { getAtlassianStudioNLCreateUrl } from '../../common/utils/atlassian-studio-url/index.tsx';
import { useAgentSitePermissions } from '../../controllers/agent-site-permissions/index.tsx';

import messages from './messages.ts';

export type AgentCreateButtonProps = {
	siteId: string;
	buttontext: MessageDescriptor;
	analyticsPayload: AnalyticsEventPayload;
	touchpointSource: string;
	isIconButton?: boolean;
	appearance?: React.ComponentProps<typeof LinkButton>['appearance'];
	iconBefore?: React.ComponentProps<typeof LinkButton>['iconBefore'];
	/**
	 * Optional override for the default "Create agent" click behaviour.
	 *
	 * When provided, the rendered button stops being a link to Atlassian
	 * Studio (no `href` / `target="_blank"`) and instead invokes this
	 * handler. The existing analytics event (`analyticsPayload`) still fires
	 * before the handler runs, so consumers see the same telemetry whether
	 * or not the override is active.
	 *
	 * Intended for products that want to keep agent creation in-app (e.g.
	 * opening a Rovo chat with `/build-agent`) instead of navigating
	 * out to Atlassian Studio.
	 */
	onClickCreateAgent?: (
		event: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const AgentCreateButton = ({
	siteId,
	buttontext,
	analyticsPayload,
	isIconButton,
	touchpointSource,
	appearance,
	iconBefore,
	onClickCreateAgent,
}: AgentCreateButtonProps): React.JSX.Element => {
	di(useAgentSitePermissions, useAnalytics, useIntl, useProfileController);
	const { email } = useProfileController();
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [rovoAgentCSID, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
		touchPoint: touchpointSource,
	});
	const { data: sitePermissions, isFetching: isFetchingSitePermissions } = useAgentSitePermissions({
		touchpointSource,
	});

	const { isAbleToCreateAgents, isFetchingPermissions } = useMemo(() => {
		return {
			isAbleToCreateAgents:
				sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions
					?.isAbleToCreateAgents ?? false,
			isFetchingPermissions: isFetchingSitePermissions,
		};
	}, [
		isFetchingSitePermissions,
		sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
	]);

	const handleAnalytics = useCallback(() => {
		fg('rovo-agents-universal-analytics') && trackCreateSessionStart();
	}, [trackCreateSessionStart]);

	const handleOverrideClick = useCallback(
		(event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			sendAnalyticsEvent(analyticsEvent.update({ ...analyticsPayload }));
			handleAnalytics();
			onClickCreateAgent?.(event, analyticsEvent);
		},
		[analyticsPayload, handleAnalytics, onClickCreateAgent, sendAnalyticsEvent],
	);

	const renderContent = () => {
		if (!isAbleToCreateAgents || isFetchingPermissions) {
			return (
				<Tooltip
					content={formatMessage(messages.createButtonDisabledTooltip)}
					canAppear={() => !isAbleToCreateAgents && !isFetchingPermissions}
				>
					<Button
						iconBefore={isFetchingPermissions ? Spinner : AddIcon}
						appearance="subtle"
						isDisabled
					>
						{formatMessage(buttontext)}
					</Button>
				</Tooltip>
			);
		}

		if (onClickCreateAgent) {
			return isIconButton ? (
				<IconButton
					icon={AddIcon}
					label={formatMessage(buttontext)}
					isTooltipDisabled={false}
					onClick={handleOverrideClick}
				/>
			) : (
				<Button appearance={appearance} iconBefore={iconBefore} onClick={handleOverrideClick}>
					{formatMessage(buttontext)}
				</Button>
			);
		}

		const commonProps: Pick<
			React.ComponentProps<typeof LinkIconButton | typeof LinkButton>,
			'href' | 'target' | 'onClick' | 'isDisabled'
		> = {
			href: getAtlassianStudioNLCreateUrl(siteId, touchpointSource, rovoAgentCSID, email),
			target: '_blank',
			onClick: (_, analyticsEvent) => {
				sendAnalyticsEvent(analyticsEvent.update({ ...analyticsPayload }));
				handleAnalytics();
			},
			isDisabled: !isAbleToCreateAgents,
		};

		return isIconButton ? (
			<LinkIconButton
				{...commonProps}
				icon={AddIcon}
				label={formatMessage(buttontext)}
				isTooltipDisabled={false}
				onAuxClick={handleAnalytics}
			/>
		) : (
			<LinkButton
				{...commonProps}
				appearance={appearance}
				iconBefore={iconBefore}
				onAuxClick={handleAnalytics}
			>
				{formatMessage(buttontext)}
			</LinkButton>
		);
	};

	return <ErrorBoundary FallbackComponent={() => null}>{renderContent()}</ErrorBoundary>;
};
